import React, {
  createContext,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";

import {
  ActionFnWithPayload,
  AnyState,
  getIdFromAction,
  ThunkAction,
} from "starfx";
import { useSchema } from "starfx/react";

import type {
  UseApiAction,
  UseApiProps,
  UseApiSimpleProps,
} from "starfx/react";
import type { ActionFn } from "starfx";

export function useFxSelector(selector) {
  // The current state of the store
  const getSnapshot = () => selector(window.fx.getState());

  // Subscribe to the store updates
  const subscribe = (callback) => window.fx.subscribe(callback);

  // Use the React hook to sync with the store
  const state = useSyncExternalStore(subscribe, getSnapshot);

  return state;
}

// Create a Context for the dispatch function
const DispatchContext = createContext(null);

// A component to provide the dispatch context
export const DispatchProvider = ({ children, store }) => {
  const dispatch = useMemo(() => store.dispatch, [store]);

  return (
    <DispatchContext.Provider value={dispatch}>
      {children}
    </DispatchContext.Provider>
  );
};

export const useFxDispatch = () => {
  const dispatch = useContext(DispatchContext);
  if (!dispatch) {
    throw new Error("useDispatch must be used within a DispatchProvider");
  }
  return dispatch;
};

export function useFxLoader<S extends AnyState>(
  action: ThunkAction | ActionFnWithPayload
) {
  const schema = useSchema();
  const id = getIdFromAction(action);
  const sel = useFxSelector((s: S) => schema.loaders.selectById(s, { id }));
  return sel;
}

export function useFxApi<P = any, A extends ThunkAction = ThunkAction<P>>(
  action: A
): UseApiAction<A>;
export function useFxApi<P = any, A extends ThunkAction = ThunkAction<P>>(
  action: ActionFnWithPayload<P>
): UseApiProps<P>;
export function useFxApi<A extends ThunkAction = ThunkAction>(
  action: ActionFn
): UseApiSimpleProps;
export function useFxApi(action: any): any {
  const dispatch = useFxDispatch();
  const loader = useFxLoader(action);
  const trigger = (p: any) => {
    if (typeof action === "function") {
      dispatch(action(p));
    } else {
      dispatch(action);
    }
  };
  return { ...loader, trigger, action };
}
